import { useState, useEffect, useCallback } from 'react';
import { SERVER_URL } from '../utils/constants';

// socket: the shared socket ref used for emits; socketInstance: the same socket as state, so the
// listener effect re-runs once a socket exists (it is only created after login) or is replaced
export function useRecordings(socket, socketInstance) {
  const [recordings, setRecordings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState(new Map()); // unitId -> { isRecording, recordingId, hasAudio }
  const [settings, setSettings] = useState({ retention_days: '30', auto_record_all: '0' });
  const [storageStats, setStorageStats] = useState(null);

  const baseUrl = SERVER_URL;
  const parseBool = (v) => v === true || v === '1' || v === 1 || v === 'true';

  // Fetch recordings list
  const fetchRecordings = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.unitId) params.append('unitId', filters.unitId);
      // The server compares start_time (a UTC ISO string) as text, so send the picked local
      // days as UTC instants covering the whole day, end day included
      if (filters.startDate) params.append('startDate', new Date(`${filters.startDate}T00:00:00`).toISOString());
      if (filters.endDate) params.append('endDate', new Date(`${filters.endDate}T23:59:59.999`).toISOString());
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await fetch(`${baseUrl}/api/recordings?${params}`);
      if (!response.ok) throw new Error('Failed to fetch recordings');

      const data = await response.json();
      setRecordings(data.recordings || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err.message);
      console.error('Error fetching recordings:', err);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  // Delete recording
  const deleteRecording = useCallback(async (id) => {
    try {
      const response = await fetch(`${baseUrl}/api/recordings/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete recording');

      // Refresh list
      fetchRecordings({ page: pagination.page, limit: pagination.limit });
      return { success: true };
    } catch (err) {
      console.error('Error deleting recording:', err);
      return { error: err.message };
    }
  }, [baseUrl, fetchRecordings, pagination]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}/api/settings`);
      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      setSettings({
        retention_days: data.retention_days ?? '30',
        auto_record_all: parseBool(data.auto_record_all) ? '1' : '0',
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, [baseUrl]);

  // Update settings
  const updateSettings = useCallback(async (newSettings) => {
    try {
      const payload = {
        ...newSettings,
        auto_record_all: parseBool(newSettings.auto_record_all) ? '1' : '0',
      };

      const response = await fetch(`${baseUrl}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to update settings');

      const data = await response.json();
      setSettings({
        retention_days: data.settings?.retention_days ?? '30',
        auto_record_all: parseBool(data.settings?.auto_record_all) ? '1' : '0',
      });
      return { success: true };
    } catch (err) {
      console.error('Error updating settings:', err);
      return { error: err.message };
    }
  }, [baseUrl]);

  // Fetch storage stats
  const fetchStorageStats = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}/api/storage/stats`);
      if (!response.ok) throw new Error('Failed to fetch storage stats');

      const data = await response.json();
      setStorageStats(data);
    } catch (err) {
      console.error('Error fetching storage stats:', err);
    }
  }, [baseUrl]);

  // Run cleanup
  const runCleanup = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}/api/storage/cleanup`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to run cleanup');

      const data = await response.json();
      // Refresh stats and recordings
      fetchStorageStats();
      fetchRecordings({ page: 1, limit: pagination.limit });
      return data;
    } catch (err) {
      console.error('Error running cleanup:', err);
      return { error: err.message };
    }
  }, [baseUrl, fetchRecordings, fetchStorageStats, pagination.limit]);

  // Delete recordings by date
  const deleteByDate = useCallback(async (date) => {
    try {
      const response = await fetch(`${baseUrl}/api/storage/by-date/${date}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete recordings');

      const data = await response.json();
      // Refresh stats and recordings
      fetchStorageStats();
      fetchRecordings({ page: 1, limit: pagination.limit });
      return data;
    } catch (err) {
      console.error('Error deleting by date:', err);
      return { error: err.message };
    }
  }, [baseUrl, fetchRecordings, fetchStorageStats, pagination.limit]);

  // Start recording via socket
  const startRecording = useCallback((unitId) => {
    return new Promise((resolve) => {
      // Not while disconnected: a buffered emit would go out on reconnect before viewer-join
      if (!socket?.current?.connected) {
        resolve({ error: 'Socket not connected' });
        return;
      }

      socket.current.emit('start-recording', { unitId }, (response) => {
        if (response.error) {
          console.error('Start recording error:', response.error);
        } else {
          setRecordingStatus(prev => {
            const newMap = new Map(prev);
            newMap.set(unitId, {
              isRecording: true,
              recordingId: response.recordingId,
              hasAudio: response.hasAudio || false,
            });
            return newMap;
          });
        }
        resolve(response);
      });
    });
  }, [socket]);

  // Stop recording via socket
  const stopRecording = useCallback((unitId) => {
    return new Promise((resolve) => {
      if (!socket?.current?.connected) {
        resolve({ error: 'Socket not connected' });
        return;
      }

      socket.current.emit('stop-recording', { unitId }, (response) => {
        if (response.error) {
          console.error('Stop recording error:', response.error);
        } else {
          setRecordingStatus(prev => {
            const newMap = new Map(prev);
            newMap.set(unitId, { isRecording: false, recordingId: null, hasAudio: false });
            return newMap;
          });
          // Refresh recordings list
          fetchRecordings({ page: 1, limit: pagination.limit });
        }
        resolve(response);
      });
    });
  }, [socket, fetchRecordings, pagination.limit]);

  // Get recording status for a unit
  const getRecordingStatus = useCallback((unitId) => {
    return recordingStatus.get(unitId) || { isRecording: false, recordingId: null, hasAudio: false };
  }, [recordingStatus]);

  // Fetch all recording statuses
  const fetchAllRecordingStatus = useCallback(() => {
    if (!socket?.current?.connected) return;

    socket.current.emit('get-all-recording-status', (response) => {
      if (response?.recordings) {
        const newMap = new Map();
        for (const rec of response.recordings) {
          newMap.set(rec.unitId, {
            isRecording: true,
            recordingId: rec.recordingId,
            hasAudio: rec.hasAudio || false,
          });
        }
        setRecordingStatus(newMap);
      }
    });
  }, [socket]);

  // Listen to socket events
  useEffect(() => {
    const s = socketInstance;
    if (!s) return;

    const handleRecordingStatus = (data) => {
      setRecordingStatus(prev => {
        const newMap = new Map(prev);
        newMap.set(data.unitId, {
          isRecording: data.isRecording,
          recordingId: data.recordingId,
          hasAudio: data.hasAudio || false,
        });
        return newMap;
      });
    };

    const handleRecordingStarted = (data) => {
      console.log('Recording started:', data);
      setRecordingStatus(prev => {
        const newMap = new Map(prev);
        newMap.set(data.unitId, {
          isRecording: true,
          recordingId: data.recordingId,
          hasAudio: data.hasAudio || false,
        });
        return newMap;
      });
    };

    const handleRecordingStopped = (data) => {
      console.log('Recording stopped:', data);
      setRecordingStatus(prev => {
        const current = prev.get(data.unitId);
        // A late stop of the previous clip must not clear the REC state of the clip after it
        if (data.recordingId && current?.recordingId && current.recordingId !== data.recordingId) return prev;
        const newMap = new Map(prev);
        newMap.set(data.unitId, { isRecording: false, recordingId: null, hasAudio: false });
        return newMap;
      });
      // Refresh recordings list
      fetchRecordings({ page: 1, limit: pagination.limit });
    };

    // Operator requests are answered only after viewer-join, which 'available-units' confirms
    // (on the first join and after every reconnect), so ask for the current statuses then.
    const handleViewerJoined = () => fetchAllRecordingStatus();

    s.on('recording-status', handleRecordingStatus);
    s.on('recording-started', handleRecordingStarted);
    s.on('recording-stopped', handleRecordingStopped);
    s.on('available-units', handleViewerJoined);

    return () => {
      s.off('recording-status', handleRecordingStatus);
      s.off('recording-started', handleRecordingStarted);
      s.off('recording-stopped', handleRecordingStopped);
      s.off('available-units', handleViewerJoined);
    };
  }, [socketInstance, fetchRecordings, fetchAllRecordingStatus, pagination.limit]);

  // Get stream URL for a recording
  const getStreamUrl = useCallback((recordingId) => {
    return `${baseUrl}/api/recordings/${recordingId}/stream`;
  }, [baseUrl]);

  // Get download URL for a recording
  const getDownloadUrl = useCallback((recordingId) => {
    return `${baseUrl}/api/recordings/${recordingId}/download`;
  }, [baseUrl]);

  return {
    // Recordings
    recordings,
    pagination,
    loading,
    error,
    fetchRecordings,
    deleteRecording,
    getStreamUrl,
    getDownloadUrl,

    // Recording control
    recordingStatus,
    startRecording,
    stopRecording,
    getRecordingStatus,

    // Settings
    settings,
    fetchSettings,
    updateSettings,

    // Storage
    storageStats,
    fetchStorageStats,
    runCleanup,
    deleteByDate,
  };
}
